import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ethers } from 'ethers';
import { buyToken, p2pInvest, submitBuyIntent } from '../../utils/api';
import { useWallet } from '../../context/WalletContext';

// ─── Minimal ERC20 ABI (only what we need: balanceOf + transfer + decimals) ──
const ERC20_ABI = [
  'function balanceOf(address owner) view returns (uint256)',
  'function transfer(address to, uint256 amount) returns (bool)',
  'function decimals() view returns (uint8)',
  'function approve(address spender, uint256 amount) returns (bool)',
  'function allowance(address owner, address spender) view returns (uint256)',
];

const BuyToken = ({ account, balance, price, onRefresh, f, fBC, BC, SYM, readOnly = false }) => {
  const { signer, provider, chainId: walletChainId, ensureChain } = useWallet();
  const [isSwitching, setIsSwitching] = useState(false);
  const autoSwitchTriedRef = useRef(false);

  // Expected chain comes from config (SystemConfig → /trade/price). Never hardcoded.
  const expectedChainId = Number(price?.networkChainId) || 56;
  const netName = price?.networkName || 'BSC Mainnet';
  const isWrongNetwork = walletChainId != null && Number(walletChainId) !== expectedChainId;

  // Build the network params for wallet_addEthereumChain from config.
  const netParams = useCallback(() => ({
    networkName: price?.networkName,
    networkRpcUrl: price?.networkRpcUrl,
    networkExplorer: price?.networkExplorer,
    nativeCurrencySymbol: price?.baseCurrency || 'BNB',
  }), [price?.networkName, price?.networkRpcUrl, price?.networkExplorer, price?.baseCurrency]);

  const switchNetwork = useCallback(async () => {
    setIsSwitching(true);
    try { await ensureChain(expectedChainId, netParams()); }
    finally { setIsSwitching(false); }
  }, [ensureChain, expectedChainId, netParams]);

  // Auto-attempt a switch once when we detect the wrong network. If the user
  // rejects, the manual button below is the fallback. A successful switch fires
  // chainChanged → the page reloads onto the correct chain.
  useEffect(() => {
    if (isWrongNetwork && !autoSwitchTriedRef.current) {
      autoSwitchTriedRef.current = true;
      switchNetwork();
    }
    if (!isWrongNetwork) autoSwitchTriedRef.current = false;
  }, [isWrongNetwork, switchNetwork]);

  const [buyAmount, setBuyAmount] = useState('');
  const [buyCurrency, setBuyCurrency] = useState(price?.baseCurrency || 'BNB');
  const [isBuying, setIsBuying] = useState(false);
  const [buyResult, setBuyResult] = useState(null);
  const [buyPending, setBuyPending] = useState(false);
  const [buyError, setBuyError] = useState('');
  const [buyUsdtAmount, setBuyUsdtAmount] = useState('');
  const [p2pInvestAmt, setP2pInvestAmt] = useState('');
  const [isP2PInvesting, setIsP2PInvesting] = useState(false);
  const [p2pResult, setP2pResult] = useState(null);
  const [p2pError, setP2pError] = useState('');
  const [walletBalance, setWalletBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);

  // ─── Helper: Get currency info from paymentCurrencies ───
  const getCurrencyInfo = useCallback((symbol) => {
    const currencies = price?.paymentCurrencies || [];
    return currencies.find(c => c.symbol.toUpperCase() === symbol.toUpperCase()) || null;
  }, [price?.paymentCurrencies]);

  // ─── Helper: Check if currency is native (BNB/ETH) or BEP20 token ───
  const isNativeCoin = useCallback((symbol) => {
    const info = getCurrencyInfo(symbol);
    // If we have info from backend, use it; otherwise fall back to known natives
    if (info) return info.isNative === true;
    return ['BNB', 'ETH'].includes(symbol.toUpperCase());
  }, [getCurrencyInfo]);

  // ─── Helper: Get contract address for a BEP20 token ───
  const getContractAddress = useCallback((symbol) => {
    const info = getCurrencyInfo(symbol);
    return info?.contractAddress || null;
  }, [getCurrencyInfo]);

  // ─── Fetch wallet balance (native or BEP20) ───
  const fetchWalletBalance = useCallback(async () => {
    if (!provider || !account) return;
    setBalanceLoading(true);
    try {
      if (isNativeCoin(buyCurrency)) {
        // Native coin (BNB/ETH) — use provider.getBalance
        const bal = await provider.getBalance(account);
        setWalletBalance(parseFloat(ethers.formatEther(bal)));
      } else {
        // BEP20 token — use contract.balanceOf
        const contractAddr = getContractAddress(buyCurrency);
        if (!contractAddr) {
          console.warn(`No contract address for ${buyCurrency}`);
          setWalletBalance(null);
          setBalanceLoading(false);
          return;
        }
        const contract = new ethers.Contract(contractAddr, ERC20_ABI, provider);
        const bal = await contract.balanceOf(account);
        // Get decimals from contract (usually 18, but check)
        let decimals = 18;
        try { decimals = await contract.decimals(); } catch { /* default 18 */ }
        setWalletBalance(parseFloat(ethers.formatUnits(bal, decimals)));
      }
    } catch (err) {
      console.error('Balance fetch error:', err);
      setWalletBalance(null);
    }
    setBalanceLoading(false);
  }, [provider, account, buyCurrency, isNativeCoin, getContractAddress]);

  // Fetch balance on mount and when currency changes
  useEffect(() => {
    fetchWalletBalance();
  }, [fetchWalletBalance]);

  // Re-fetch after successful buy
  useEffect(() => {
    if (buyResult) fetchWalletBalance();
  }, [buyResult, fetchWalletBalance]);

  const curRate = price?.cryptoPrices?.[buyCurrency] || 0;
  const tokenPrice = price?.tokenPriceInUsdt || 0;
  const amt = parseFloat(buyAmount) || 0;
  const usdtEq = buyCurrency === 'USDT' ? amt : amt * curRate;
  const isRoot = balance?.roboId === 'ROBO000001';
  const payoutPct = isRoot ? 1.0 : (price?.payoutPercent || 60) / 100;

  // ─── POST-BUY (market-after) pricing — must match the backend exactly ───
  // The buyer is priced at the market rate that WILL exist after this purchase.
  // Fixed point:  totalTokens = usdt·S0 / (L0 + liqAdd − payout%·usdt),
  // which settles exactly at the resulting price (usdt / totalTokens).
  const LIQ_FRACTION = 0.7;                      // share of payment that becomes liquidity (matches backend)
  const S0 = price?.totalSupply || 0;
  const L0 = price?.liquidity || 0;
  const payoutFrac = (price?.payoutPercent || 60) / 100;
  let totalTokens;
  if (isRoot || S0 <= 0) {
    totalTokens = tokenPrice > 0 ? usdtEq / tokenPrice : 0;
  } else {
    const denom = L0 + usdtEq * LIQ_FRACTION - payoutFrac * usdtEq;
    totalTokens = denom > 0 ? (usdtEq * S0) / denom : (tokenPrice > 0 ? usdtEq / tokenPrice : 0);
  }
  const tokensToUser = totalTokens * payoutPct;
  // Effective (post-buy) price shown to the user — the rate they actually buy at.
  const effPrice = totalTokens > 0 ? usdtEq / totalTokens : tokenPrice;
  const minUsdt = price?.minBuyUsdt || 10;
  const bcRate = price?.cryptoPrices?.[BC] || 1;
  const minBC = BC === 'USDT' ? minUsdt : (bcRate > 0 ? minUsdt / bcRate : 0);
  const isUnderMin = amt > 0 && usdtEq < minUsdt;
  const usdtInput = parseFloat(buyUsdtAmount) || 0;
  const cryptoNeeded = curRate > 0 ? (usdtInput / curRate) : 0;

  // ─── Rolling per-period buy cap (all amounts here are in USDT) ───
  const isLimited = !balance?.buyCapExempt
    && (balance?.buyLimitMode || price?.buyLimitMode) === 'limited'
    && ((balance?.buyCapAmountUsdt != null ? balance.buyCapAmountUsdt : price?.maxBuyUsdt) || 0) > 0;
  const maxUsdt = balance?.buyCapAmountUsdt != null ? balance.buyCapAmountUsdt : (price?.maxBuyUsdt || 0);
  const periodMonths = balance?.buyCapPeriodMonths || price?.buyCapPeriodMonths || 6;
  // remaining allowance for THIS period (USDT); backend already resets it when the window expires
  const remaining = isLimited
    ? (balance?.buyPeriodRemaining != null ? balance.buyPeriodRemaining : maxUsdt)
    : Infinity;
  const usedThisPeriod = Math.max(0, maxUsdt - (Number.isFinite(remaining) ? remaining : maxUsdt));
  const resetsAt = balance?.buyPeriodExpiresAt ? new Date(balance.buyPeriodExpiresAt) : null;
  const fmtDate = (d) => d ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
  const isMaxedOut = isLimited && remaining <= 0;
  const isOverMax = isLimited && amt > 0 && usdtEq > remaining;

  // ─── Wallet balance validation (gas-aware for native coins) ───
  const GAS_RESERVE = isNativeCoin(buyCurrency) ? 0.0008 : 0;
  const spendableBal = walletBalance != null ? Math.max(0, walletBalance - GAS_RESERVE) : null;
  const isOverBalance = spendableBal != null && amt > 0 && amt > spendableBal + 1e-12;
  const buyPaused = price?.isBuyPaused === true;

  // Largest buyable = min(spendable wallet balance, remaining allowance in this currency)
  const remainingInCurrency = !isLimited
    ? Infinity
    : (buyCurrency === 'USDT' ? remaining : (curRate > 0 ? remaining / curRate : Infinity));
  const maxBuyable = Math.min(spendableBal != null ? spendableBal : Infinity, remainingInCurrency);

  // ─── Handle Buy ───
  const handleBuy = async () => {
      if (readOnly) { setBuyError('Read-only admin view — actions are disabled.'); return; }
      if (!buyAmount || parseFloat(buyAmount) <= 0) { setBuyError('Enter a valid amount'); return; }

      // ─── FRONTEND VALIDATION (before MetaMask opens) ───
      if (buyPaused) { setBuyError(`Buying is currently paused by the ${SYM} team.`); return; }
      if (isOverBalance) {
        setBuyError(isNativeCoin(buyCurrency)
          ? `Not enough ${buyCurrency} — leave a little for gas (max usable ~${f(spendableBal, 6)} ${buyCurrency}).`
          : `Insufficient ${buyCurrency} balance. You have ${f(walletBalance, 6)} ${buyCurrency}.`);
        return;
      }
      if (isUnderMin) { setBuyError(`Minimum purchase is ${f(minBC, 4)} ${BC}`); return; }
      if (isOverMax) { setBuyError(`Exceeds remaining limit (${f(remaining, 2)} USDT this period)`); return; }

      setIsBuying(true); setBuyError(''); setBuyResult(null); setBuyPending(false);
      try {
        const isTestMode = price?.testMode === true;
        let txHash = null;

        // On-chain transfer (ALL currencies in production — including USDT)
        if (!isTestMode) {
          if (!signer) { setBuyError('Wallet not connected'); setIsBuying(false); return; }
          const mainWallet = price?.mainWallet;
          if (!mainWallet) { setBuyError('Main wallet not configured'); setIsBuying(false); return; }

          // Make sure we're on the right chain before asking MetaMask to send funds.
          if (isWrongNetwork) {
            const ok = await switchNetwork();
            if (!ok || isWrongNetwork) {
              setBuyError(`Please switch to ${netName} (${expectedChainId}) to continue.`);
              setIsBuying(false); return;
            }
          }

          try {
            if (isNativeCoin(buyCurrency)) {
              // ─── NATIVE (BNB/ETH): value transfer ───
              const amountWei = ethers.parseEther(parseFloat(buyAmount).toFixed(15));
              const tx = await signer.sendTransaction({ to: mainWallet, value: amountWei });
              txHash = tx.hash;
              // Durably record the payment the instant it's broadcast — survives
              // a closed tab / dropped network so the buy can never be lost.
              submitBuyIntent({ walletAddress: account, txHash, amount: parseFloat(buyAmount), currency: buyCurrency });
              await tx.wait();
            } else {
              // ─── BEP20 TOKEN (PVC/USDT/MATIC): ERC20 transfer() ───
              const contractAddr = getContractAddress(buyCurrency);
              if (!contractAddr) {
                setBuyError(`${buyCurrency} contract address not configured. Contact the ${SYM} team.`);
                setIsBuying(false); return;
              }
              const contract = new ethers.Contract(contractAddr, ERC20_ABI, signer);
              let decimals = 18;
              try { decimals = await contract.decimals(); } catch {}
              const amountUnits = ethers.parseUnits(parseFloat(buyAmount).toFixed(6), decimals);
              const tx = await contract.transfer(mainWallet, amountUnits);
              txHash = tx.hash;
              // Durably record the payment the instant it's broadcast.
              submitBuyIntent({ walletAddress: account, txHash, amount: parseFloat(buyAmount), currency: buyCurrency });
              await tx.wait();
            }
          } catch (txErr) {
            if (txErr.code === 'ACTION_REJECTED' || txErr.code === 4001) setBuyError('Transaction rejected');
            else if (txErr.code === 'INSUFFICIENT_FUNDS') setBuyError(`Insufficient ${buyCurrency} (need amount + gas in BNB)`);
            else if (txErr.reason?.includes('transfer amount exceeds balance')) setBuyError(`Insufficient ${buyCurrency} balance`);
            else setBuyError(txErr.shortMessage || txErr.message || 'Transaction failed');
            setIsBuying(false); return;
          }
        }

        // Only reaches here AFTER MetaMask confirmed (or in test mode)
        const r = await buyToken({ walletAddress: account, amount: parseFloat(buyAmount), currency: buyCurrency, txHash });
        // 202 → payment received but still confirming; the reconciler will credit
        // it automatically. Show a friendly pending state instead of an error.
        if (r && r.pending) {
          setBuyPending(true);
        } else {
          setBuyResult(r.transaction);
        }
        setBuyAmount(''); onRefresh();
        fetchWalletBalance();
      } catch (e) {
        if (e.code === 'ACTION_REJECTED' || e.code === 4001) setBuyError('Transaction rejected');
        else if (txHash) {
          // Payment was already broadcast, but crediting didn't finish in this
          // request. It's recorded server-side and will be auto-credited — so
          // reassure the user rather than showing a scary error (don't pay again).
          setBuyPending(true);
        } else setBuyError(e.response?.data?.error || e.message || 'Purchase failed');
      }
      setIsBuying(false);
    };

  return (
    <div className="fade-in bt-root">
      <style>{`
        .bt-root .d-trade.card { width: 100%; box-sizing: border-box; }
        .bt-root .input:disabled { opacity: .55; cursor: not-allowed; }
        .bt-root .d-max-btn:disabled { opacity: .5; cursor: not-allowed; }
        .bt-root .btn:disabled { opacity: .55; cursor: not-allowed; }
        @media (max-width: 520px) {
          .bt-root .d-form-row { flex-direction: column; gap: 12px; }
          .bt-root .d-form-row .d-field { width: 100% !important; flex: 1 1 auto !important; }
          .bt-root .d-rates-bar { gap: 10px; }
          .bt-root .d-reverse-row { flex-wrap: wrap; }
        }
      `}</style>
      <div className="d-trade card" style={{ maxWidth: 540 }}>
        <h3>Buy {SYM} Tokens</h3>
        {buyPaused && <div className="notice notice-warning">Buying is currently paused by the {SYM} team.</div>}

        {isMaxedOut && (
          <div className="d-maxed-out">
            <div className="d-maxed-icon">🚫</div>
            <h4>Buy Limit Reached</h4>
            <p>You've used <strong>{f(usedThisPeriod, 2)} USDT</strong> of your <strong>{f(maxUsdt, 2)} USDT</strong> cap for this {periodMonths}-month period.</p>
            {resetsAt && <p>Resets on <strong>{fmtDate(resetsAt)}</strong> — fresh {f(maxUsdt, 2)} USDT then.</p>}
          </div>
        )}

        {!isMaxedOut && (<>
          <div className="d-rates-bar">
            <div className="d-rate-item"><span>Token Price</span><strong className="text-accent">{f(tokenPrice, 6)} USDT</strong></div>
            {curRate > 0 && buyCurrency !== 'USDT' && <div className="d-rate-item"><span>1 {buyCurrency}</span><strong>{f(curRate, 2)} USDT</strong></div>}
            <div className="d-rate-item"><span>Min Buy</span><strong>{f(minBC, 4)} {BC}</strong></div>
            {isLimited ? (
              <div className="d-rate-item"><span>Remaining ({periodMonths}mo)</span><strong className="text-accent">{f(remaining, 2)} USDT</strong></div>
            ) : (
              <div className="d-rate-item"><span>Limit</span><strong>Unlimited</strong></div>
            )}
          </div>
          {isLimited && resetsAt && !isMaxedOut && (
            <p className="d-trade-sub" style={{ marginTop: 4 }}>Cap {f(maxUsdt, 2)} USDT per {periodMonths} months · resets {fmtDate(resetsAt)}</p>
          )}

          {/* Wallet Balance — now reads BEP20 token balance correctly */}
          {walletBalance !== null && (
            <div className="d-wallet-balance-bar">
              <div className="d-wallet-balance-info">
                <span className="d-wallet-balance-label">Your Wallet Balance</span>
                <strong className="d-wallet-balance-amount">
                  {balanceLoading ? '...' : f(walletBalance, 6)} {buyCurrency}
                </strong>
              </div>
              {walletBalance > (isNativeCoin(buyCurrency) ? 0.001 : 0) ? (
                <button className="btn btn-primary btn-sm" onClick={() => {
                  // For native coins, reserve gas; for tokens, can use full balance
                  const maxUse = isNativeCoin(buyCurrency) ? Math.max(0, walletBalance - 0.001) : walletBalance;
                  setBuyAmount(maxUse.toFixed(6)); setBuyError(''); setBuyResult(null);
                }}>Use All</button>
              ) : (
                <span className="text-red" style={{ fontSize: 12 }}>Insufficient</span>
              )}
            </div>
          )}
          {walletBalance === null && !balanceLoading && (
            <div className="d-wallet-balance-bar">
              <div className="d-wallet-balance-info">
                <span className="d-wallet-balance-label">Your Wallet Balance</span>
                <strong className="d-wallet-balance-amount">—</strong>
              </div>
            </div>
          )}

          {/* Chain warning — dynamic (chain comes from config), with auto/manual switch */}
          {isWrongNetwork && (
            <div className="notice notice-error" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
              <span>Wrong network! Switch to <strong>{netName} ({expectedChainId})</strong>. Current: {walletChainId}.</span>
              <button className="btn btn-sm btn-primary" onClick={switchNetwork} disabled={isSwitching}>
                {isSwitching ? 'Switching…' : `Switch to ${netName}`}
              </button>
            </div>
          )}

          <div className="d-form">
            {buyCurrency !== 'USDT' && curRate > 0 && (
              <div className="d-reverse-calc">
                <label>I have USDT, how much {buyCurrency} do I need?</label>
                <div className="d-reverse-row">
                  <input type="number" step="1" placeholder="e.g. 100" value={buyUsdtAmount} onChange={e => setBuyUsdtAmount(e.target.value)} className="input" />
                  <span className="d-reverse-eq">=</span>
                  <span className="d-reverse-result mono">{usdtInput > 0 ? `${f(cryptoNeeded, 6)} ${buyCurrency}` : `— ${buyCurrency}`}</span>
                  {usdtInput > 0 && <button className="btn btn-outline btn-sm" onClick={() => { setBuyAmount(cryptoNeeded.toFixed(6)); setBuyUsdtAmount(''); }}>Use</button>}
                </div>
              </div>
            )}

            <div className="d-form-row">
              <div className="d-field" style={{ flex: 1 }}>
                <label>You Pay ({buyCurrency})</label>
                <div className="d-field-with-max">
                  <input type="number" step="0.0001" placeholder="0.00" value={buyAmount} disabled={buyPaused} onChange={e => { setBuyAmount(e.target.value); setBuyError(''); setBuyResult(null); }} className="input" />
                  {Number.isFinite(maxBuyable) && maxBuyable > 0 && (
                    <button className="d-max-btn" disabled={buyPaused} onClick={() => { setBuyAmount(maxBuyable.toFixed(6)); setBuyError(''); setBuyResult(null); }}>MAX</button>
                  )}
                </div>
              </div>
              <div className="d-field" style={{ width: 100 }}><label>Currency</label>
                <select value={buyCurrency} onChange={e => { setBuyCurrency(e.target.value); setWalletBalance(null); }} className="input">
                  {price?.paymentCurrencies?.map(c => <option key={c.symbol} value={c.symbol}>{c.symbol}</option>) || <option value="BNB">BNB</option>}
                </select>
              </div>
            </div>

            {amt > 0 && (
              <div className="d-calc">
                <div className="d-calc-row"><span>You Pay</span><strong>{f(amt, 6)} {buyCurrency}</strong></div>
                {buyCurrency !== 'USDT' && <div className="d-calc-row"><span>USDT Value</span><strong>{f(usdtEq, 2)} USDT</strong></div>}
                {!isRoot && <div className="d-calc-row"><span>Buy Price (after your buy)</span><strong>{f(effPrice, 6)} USDT</strong></div>}
                <div className="d-calc-row d-calc-highlight"><span>You Receive</span><strong className="text-accent">{f(tokensToUser, 2)} {SYM}</strong></div>
              </div>
            )}

            {isUnderMin && <div className="notice notice-error">Minimum is {f(minBC, 4)} {BC}.</div>}
            {isOverMax && <div className="notice notice-error">Exceeds remaining limit ({f(remaining, 2)} USDT this period).</div>}
            {isOverBalance && (
              <div className="notice notice-error">
                {isNativeCoin(buyCurrency)
                  ? `Leave a little ${buyCurrency} for gas — max usable ~${f(spendableBal, 6)} ${buyCurrency}.`
                  : `Exceeds your ${buyCurrency} balance (${f(walletBalance, 6)} ${buyCurrency}).`}
              </div>
            )}
            {buyError && <div className="notice notice-error">{buyError}</div>}
            {buyResult && <div className="notice notice-success">Purchased <strong>{f(buyResult.tokensReceived, 2)} {SYM}</strong> for {f(buyResult.amountPaid || buyResult.usdtEquivalent, 4)} {buyResult.paymentCurrency || BC}</div>}
            {buyPending && <div className="notice notice-info">Payment received ✓ — your {SYM} is being confirmed on-chain and will be credited to your balance within a few minutes. Please don't pay again.</div>}

            <button className="btn btn-primary btn-lg" onClick={handleBuy} disabled={readOnly || isBuying || buyPaused || isUnderMin || isOverMax || isOverBalance || amt <= 0 || isWrongNetwork}>
              {readOnly ? 'Read-only view' : isBuying ? 'Processing...' : buyPaused ? 'Buying Paused' : isWrongNetwork ? `Switch to ${netName}` : `Buy ${SYM}`}
            </button>
          </div>
        </>)}
      </div>

      {/* P2P Invest Block */}
      {(balance?.p2pWallet || 0) > 0 && (() => {
        const p2pBal = balance.p2pWallet;
        const p2pVal = p2pBal * tokenPrice;
        const invAmt = parseFloat(p2pInvestAmt) || 0;
        const invVal = invAmt * tokenPrice;
        const invTokens = invVal > 0 && tokenPrice > 0 ? (invVal / tokenPrice) * payoutPct : 0;

        const handleP2PInvest = async () => {
          if (readOnly) { setP2pError('Read-only admin view — actions are disabled.'); return; }
          if (invAmt <= 0 || invAmt > p2pBal) { setP2pError('Invalid amount'); return; }
          setIsP2PInvesting(true); setP2pError(''); setP2pResult(null);
          try {
            const r = await p2pInvest({ walletAddress: account, tokenAmount: invAmt });
            setP2pResult(r.transaction); setP2pInvestAmt(''); onRefresh();
          } catch (e) { setP2pError(e.response?.data?.error || 'Failed'); }
          setIsP2PInvesting(false);
        };

        return (
          <div className="d-trade card" style={{ maxWidth: 540, marginTop: 16 }}>
            <h3>Invest from P2P Wallet</h3>
            <p className="d-trade-sub">You have <strong>{f(p2pBal, 2)} {SYM}</strong> ({fBC(p2pVal, 4)} {BC}). Invest to activate full allocation.</p>
            <div className="d-form">
              <div className="d-field">
                <label>{SYM} to Invest</label>
                <div className="d-field-with-max">
                  <input type="number" placeholder="0.00" value={p2pInvestAmt} onChange={e => { setP2pInvestAmt(e.target.value); setP2pError(''); setP2pResult(null); }} className="input" />
                  <button className="d-max-btn" onClick={() => setP2pInvestAmt(p2pBal > 0 ? p2pBal.toFixed(2) : '0')}>MAX</button>
                </div>
              </div>
              {invAmt > 0 && (
                <div className="d-calc">
                  <div className="d-calc-row"><span>You Invest</span><strong>{f(invAmt, 2)} {SYM}</strong></div>
                  <div className="d-calc-row"><span>Value</span><strong>{fBC(invVal, 4)} {BC}</strong></div>
                  <div className="d-calc-row d-calc-highlight"><span>You Receive ({(payoutPct * 100).toFixed(0)}%)</span><strong className="text-accent">{f(invTokens, 2)} {SYM}</strong></div>
                </div>
              )}
              {p2pError && <div className="notice notice-error">{p2pError}</div>}
              {p2pResult && <div className="notice notice-success">Invested <strong>{f(p2pResult.investedTokens, 2)} {SYM}</strong>. Received <strong>{f(p2pResult.tokensReceived, 2)} {SYM}</strong>.</div>}
              <button className="btn btn-primary btn-lg" onClick={handleP2PInvest} disabled={readOnly || isP2PInvesting || invAmt <= 0 || invAmt > p2pBal || price?.isBuyPaused}>
                {readOnly ? 'Read-only view' : isP2PInvesting ? 'Investing...' : 'Invest from P2P'}
              </button>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default BuyToken;